// src/pages/Admin/Coaching/Coaching.jsx
import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import useScrollToTop from "../../../hooks/useScollToTop";
import { getAllEnseignants } from "../../../services/adherentServices";
import { getTeacherEvents, unregisterAdherent } from "../../../services/eventServices";
import EditHoursModal from "./EditHoursModal";
import CoachingSummary from "./CoachingSummary";
import CoachingHoursChart from "./CoachingHoursChart";
import { FaUserTie } from "react-icons/fa";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

export default function Coaching() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [events, setEvents] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getAllEnseignants();
        setTeachers(Array.isArray(data) ? data : []);
      } catch {
        toast.error("Erreur lors de la récupération des enseignants");
      } finally {
        setLoadingTeachers(false);
      }
    })();
  }, []);

  const fetchEventsForTeacher = async (teacherId) => {
    setLoadingEvents(true);
    try {
      const eventsData = await getTeacherEvents(teacherId);
      setEvents(eventsData?.Events || []);
    } catch {
      toast.error("Erreur lors de la récupération des événements");
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleTeacherChange = async (e) => {
    const teacherId = e.target.value;
    setSelectedTeacher(teacherId);
    setEvents([]);
    if (teacherId) await fetchEventsForTeacher(teacherId);
  };

  const openEditModal = (event) => {
    setCurrentEvent(event);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    if (selectedTeacher) fetchEventsForTeacher(selectedTeacher);
  };

  const handleUnregisterAdherent = async (adherentId, eventId) => {
    if (
      !window.confirm(
        "Voulez-vous vraiment supprimer ce coach de l'événement ? Cela supprimera également les heures de coaching enregistrées."
      )
    )
      return;

    try {
      await unregisterAdherent(adherentId, eventId);
      toast.success("Coach supprimé de l'événement avec succès");
      if (selectedTeacher) fetchEventsForTeacher(selectedTeacher);
    } catch (error) {
      console.error("Unregister error:", error);
      toast.error("Erreur lors de la suppression du coach de l'événement");
    }
  };

  return (
    <section className="w-full">
      {/* ✅ même offset que HeuresEnseignement (évite le recouvrement navbar) */}
      <div className="mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px]" />

      <div className="mx-auto w-full px-3 sm:px-6 max-w-7xl">
        {/* Header page */}
                <HeaderTitle
                  role="Administrateur"
                  title="Gestion des heures de coaching"
                  align="center"
                  className="mb-1"
                />

        {/* Navigation */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* 🧊 Carte principale – même structure que HeuresEnseignement */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau header gradient (même rendu) */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center text-white">
                  <FaUserTie />
                </div>
                <div>
                  <p className="text-white text-lg sm:text-xl font-semibold">
                    Synthèse & suivi des heures de coaching
                  </p>
                  <p className="text-white/90 text-sm">
                    Choisissez un enseignant pour voir ses événements et saisir ses heures.
                  </p>
                  <p className="text-white/90 text-sm">
                    La création des heures de coaching se fait depuis la page Suivi Compétitions de l'Enseignant (Fenêtre Résultat)
                  </p>
                </div>
              </div>

              {/* Sélecteur enseignant – style “bouton blanc” comme les CTA de HeuresEnseignement */}
              <div className="w-full sm:w-auto">
                <select
                  value={selectedTeacher}
                  onChange={handleTeacherChange}
                  disabled={loadingTeachers}
                  className="w-full sm:w-[320px] rounded-xl border-0 bg-white text-slate-900 px-3 py-2 font-medium shadow-md focus:outline-none focus:ring-2 focus:ring-white/70"
                >
                  <option value="">
                    {loadingTeachers ? "Chargement..." : "Sélectionnez un enseignant"}
                  </option>
                  {teachers.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nom} {t.prenom}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Contenu de la carte */}
          <div className="p-4 sm:p-6 space-y-6">
            {/* Graphique – panneau neutre (pas de gros dégradé) */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
              <CoachingHoursChart />
            </div>

            {/* Résultats / Table */}
            {!selectedTeacher && (
              <div className="text-center py-12">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 via-violet-500 to-pink-500 opacity-80" />
                <h3 className="mt-6 text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Choisissez un enseignant pour commencer
                </h3>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  Les événements et les heures de coaching s’afficheront ici.
                </p>
              </div>
            )}

            {selectedTeacher && (
              <>
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40">
                  <table className="w-full md:table-fixed">
                    <thead>
                      <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
                        {["Date", "Événement", "Heures de présence", "Actions"].map((h) => (
                          <th
                            key={h}
                            className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-white"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                      {loadingEvents && (
                        <tr>
                          <td
                            colSpan={4}
                            className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400"
                          >
                            Chargement des événements…
                          </td>
                        </tr>
                      )}

                      {!loadingEvents && events.length === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400"
                          >
                            Aucun événement trouvé.
                          </td>
                        </tr>
                      )}

                      {!loadingEvents &&
                        events.map((event) => (
                          <tr
                            key={event.id}
                            className="transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                          >
                            <td className="px-4 py-3 text-sm text-slate-800 dark:text-slate-100">
                              {new Date(event.start).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                              {event.titre}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                              {event?.AdherentEvent?.coach_hour ?? "—"}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <ButtonEdit onClick={() => openEditModal(event)} />
                                <ButtonDelete
                                  onClick={() =>
                                    handleUnregisterAdherent(
                                      event?.AdherentEvent?.adherent_id,
                                      event?.AdherentEvent?.event_id
                                    )
                                  }
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {!!events.length && <CoachingSummary events={events} />}
              </>
            )}
          </div>
        </div>
      </div>

      {isEditModalOpen && currentEvent && (
        <EditHoursModal
          isOpen={isEditModalOpen}
          onClose={closeEditModal}
          event={currentEvent}
          refreshEvents={() => fetchEventsForTeacher(selectedTeacher)}
        />
      )}

      <ToastContainer />
    </section>
  );
}
